import { PoserClock } from "../../util/clock";

import {
  deserialise,
  WorkerGetTimeLimits,
  WorkerInit,
  WorkerMsg,
  WorkerSetToken,
  WorkerTeardown,
  WorkerUpdate,
} from "./msg";

import { FrameBufferManager } from "../frame-buffer-manager";

import { fromMainContext } from "../context";
import { Context } from "../../context";
import { ManifestLimits } from "../TrackingDataManager";
import { ensureFlagIsSet } from "../../../lib/misc/util";

export class MainChannel {
  private initialised = false;

  private frameManager?: FrameBufferManager;

  private worker: Worker;

  private prevClock?: PoserClock;

  private resolves: {
    init?: (value: ManifestLimits | undefined) => void;
    live?: (value: ManifestLimits | undefined) => void;
  } = {};

  constructor(worker: Worker) {
    this.worker = worker;
  }

  async init(context: Context): Promise<ManifestLimits | undefined> {
    this.frameManager = new FrameBufferManager(context);
    const workerContext = fromMainContext(context);
    if (!workerContext) {
      console.error("can't transform main context");
      return;
    }

    const msg: WorkerInit = {
      type: "init",
      data: {
        context: workerContext,
        clock: context.clock,
      },
    };
    this.sendMessage(msg);

    this.worker.onmessage = ({ data }: any) => {
      const msg = deserialise(data);

      this.parseMessage(msg);
    };

    return new Promise<ManifestLimits | undefined>((resolve) => {
      this.resolves.init = resolve;
    });
  }

  async getLimits(): Promise<ManifestLimits | undefined> {
    await ensureFlagIsSet(this, "initialised");

    const msg: WorkerGetTimeLimits = {
      type: "get-time-limits",
    };
    this.sendMessage(msg);

    return new Promise<ManifestLimits | undefined>((resolve) => {
      this.resolves.live = resolve;
    });
  }

  // TODO: make this async and awaitable??
  update(clock: PoserClock, lowTrackingFps = false) {
    if (!this.initialised) return;

    // TODO: if we want to use intermediate buffering in main thread,
    //       then we have to tick here
    // this.frameManager?.tick();

    if (this.prevClock && this.prevClock.equals(clock)) {
      return;
    } else {
      this.prevClock = PoserClock.clone(clock);
    }
    const msg: WorkerUpdate = {
      type: "update",
      data: {
        clock: clock,
        lastFrameNum: this.frameManager?.lastFrameNum,
        lowTrackingFps,
      },
    };
    this.sendMessage(msg);
  }

  setBearerToken(token: string) {
    const msg: WorkerSetToken = { type: "set-token", data: { token } };
    this.sendMessage(msg);
  }

  teardown() {
    const msg: WorkerTeardown = { type: "teardown" };
    this.sendMessage(msg);
    this.worker.onmessage = null;
    this.worker.terminate();
  }

  private sendMessage(msg: WorkerMsg) {
    this.worker.postMessage(msg);
  }

  private async parseMessage(msg: WorkerMsg) {
    switch (msg.type) {
      case "return-init":
        // so we can wait on the main thread for initialisation of worker
        this.initialised = true;
        if (this.resolves.init) this.resolves.init(msg.data);
        break;

      case "return-get-time-limits":
        if (this.resolves.live) this.resolves.live(msg.data);
        break;

      case "return-update":
        this.frameManager?.update(msg.data);
        break;

      default:
        console.error(`invalid msg type ${msg.type} on main thread`);
        break;
    }
  }
}
